/*
 * school-zones.js
 * Ported near-verbatim from the approved design mockup
 * (Finalized-Tools/PrimarySchoolProfileList.html) — search/filter/sort/
 * render/browse-by-area logic is unchanged. What's different from the
 * mockup: data comes from two <script type="application/json"> tags
 * rendered server-side from page.metafields.szl.* (school-zones-tool.liquid),
 * not a hardcoded blob, and every element id is suffixed with the section's
 * id so this still works if the section is ever duplicated on the page.
 *
 * DISTRICTS / TOWNS below are deliberately static, same as the mockup —
 * they're the standard URA postal-district / HDB-town reference lists used
 * site-wide, not derived from the school dataset. Values link out to
 * already-live district/town school hub pages (schools-district-N /
 * schools-{town-slug}), never rebuilt here.
 */
(function () {
  var scriptEl = document.currentScript;
  if (!scriptEl) return;
  var sectionId = scriptEl.dataset.sectionId;

  function byId(suffix) {
    return document.getElementById(suffix + '-' + sectionId);
  }

  var dataEl = byId('szt-data');
  var slugsEl = byId('szt-slugs');
  if (!dataEl || !slugsEl) return;

  var rows;
  var SCHOOL_SLUGS;
  try {
    rows = JSON.parse(dataEl.textContent);
    SCHOOL_SLUGS = JSON.parse(slugsEl.textContent);
  } catch (e) {
    return;
  }
  rows.sort(function (a, b) { return (b.r2c || 0) - (a.r2c || 0); });

  var activeFilter = 'all';
  var query = '';

  function badgeClass(d) {
    return { 'Very High': 'vhigh', 'High': 'high', 'Medium': 'medium', 'Low': 'low' }[d] || 'low';
  }

  // Simplified "chance of a place" read: vacancies / applicants at 2C,
  // capped at 100%. Schools with no balloting mean everyone who applied got
  // in, so those read 100% regardless of the raw ratio. Deliberately a
  // quick-glance estimate, not MOE's real per-distance-tier ballot odds
  // (see the methodology section on this page).
  function chancePct(r) {
    if (!r.ballot) return 100;
    if (!r.a2c) return 100;
    return Math.max(1, Math.min(100, Math.round((r.v2c / r.a2c) * 100)));
  }

  function profileLinkHtml(name) {
    var slug = SCHOOL_SLUGS[name];
    if (!slug) return '<span class="szt__profile-link szt__profile-link--muted">Coming soon</span>';
    return '<a class="szt__profile-link" href="/pages/' + slug + '">Read profile &rarr;</a>';
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function render() {
    var tbody = byId('szt-tbody');
    var emptyEl = byId('szt-empty');
    var countEl = byId('szt-count');
    if (!tbody) return;

    var filtered = rows.filter(function (r) {
      if (activeFilter !== 'all' && r.demand !== activeFilter) return false;
      if (query && r.n.toLowerCase().indexOf(query) === -1) return false;
      return true;
    });

    if (countEl) countEl.textContent = filtered.length + ' of ' + rows.length + ' schools';
    if (emptyEl) emptyEl.style.display = filtered.length ? 'none' : 'block';

    tbody.innerHTML = filtered.map(function (r) {
      var pct = chancePct(r);
      var r2cDisplay = (r.r2c === null || r.r2c === undefined) ? '—' : r.r2c.toFixed(2) + '×';
      return '<tr>' +
        '<td class="szt__sname">' + escapeHtml(r.n) + '</td>' +
        '<td class="szt__num">' + (r.v2c != null ? r.v2c : '—') + '</td>' +
        '<td class="szt__num">' + (r.a2c != null ? r.a2c : '—') + '</td>' +
        '<td class="szt__num szt__ratio-cell">' + r2cDisplay + '</td>' +
        '<td>' + (r.ballot ? 'Yes' : '—') + '</td>' +
        '<td><div class="szt__chance-cell"><div class="szt__chance-bar"><i style="width:' + pct + '%"></i></div><span class="szt__chance-pct">' + pct + '%</span></div></td>' +
        '<td><span class="szt__badge szt__badge--' + badgeClass(r.demand) + '">' + escapeHtml(r.demand) + '</span></td>' +
        '<td>' + profileLinkHtml(r.n) + '</td>' +
        '</tr>';
    }).join('');
  }

  var searchEl = byId('szt-search');
  if (searchEl) {
    searchEl.addEventListener('input', function (e) {
      query = e.target.value.trim().toLowerCase();
      render();
    });
  }

  var filtersEl = byId('szt-filters');
  if (filtersEl) {
    filtersEl.addEventListener('click', function (e) {
      var btn = e.target.closest('.szt__filter');
      if (!btn) return;
      filtersEl.querySelectorAll('.szt__filter').forEach(function (b) {
        b.classList.remove('szt__filter--active');
      });
      btn.classList.add('szt__filter--active');
      activeFilter = btn.dataset.f;
      render();
    });
  }

  // Browse by district/town — links out to the existing, already-built
  // "Schools Near District N" / "Schools Near {Town}" hub pages rather than
  // re-deriving a school -> district mapping this dataset doesn't carry.
  var DISTRICTS = [
    [1, 'Raffles Place, Marina, Cecil'], [2, 'Tanjong Pagar, Chinatown'], [3, 'Alexandra, Commonwealth, Queenstown'],
    [4, 'Harbourfront, Telok Blangah, Sentosa'], [5, 'Buona Vista, West Coast, Clementi'], [6, 'City Hall, Clarke Quay'],
    [7, 'Beach Road, Bugis, Rochor'], [8, 'Farrer Park, Little India'], [9, 'Orchard, River Valley'],
    [10, 'Tanglin, Holland, Bukit Timah'], [11, 'Newton, Novena'], [12, 'Balestier, Toa Payoh'],
    [13, 'Macpherson, Potong Pasir'], [14, 'Geylang, Paya Lebar'], [15, 'Katong, Joo Chiat, Amber Rd'],
    [16, 'Bedok, Upper East Coast'], [17, 'Changi, Loyang'], [18, 'Tampines, Pasir Ris'],
    [19, 'Serangoon Gardens, Punggol, Hougang'], [20, 'Bishan, Ang Mo Kio'], [21, 'Upper Bukit Timah, Clementi Park'],
    [22, 'Jurong'], [23, 'Hillview, Dairy Farm, Bukit Panjang, Choa Chu Kang'], [24, 'Lim Chu Kang, Tengah'],
    [25, 'Kranji, Woodlands, Woodgrove'], [26, 'Upper Thomson, Springleaf'], [27, 'Yishun, Sembawang'], [28, 'Seletar']
  ];
  var TOWNS = ['Ang Mo Kio', 'Bedok', 'Bishan', 'Bukit Batok', 'Bukit Merah', 'Bukit Panjang', 'Bukit Timah',
    'Central Area', 'Choa Chu Kang', 'Clementi', 'Geylang', 'Hougang', 'Jurong East', 'Jurong West',
    'Kallang/Whampoa', 'Marine Parade', 'Pasir Ris', 'Punggol', 'Queenstown', 'Sembawang', 'Sengkang',
    'Serangoon', 'Tampines', 'Tengah', 'Toa Payoh', 'Woodlands', 'Yishun'];

  function slugifyTown(t) {
    return t.toLowerCase().replace(/\//g, '-').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  var districtGroup = byId('szt-browse-districts');
  if (districtGroup) {
    DISTRICTS.forEach(function (pair) {
      var o = document.createElement('option');
      o.value = '/pages/schools-district-' + pair[0];
      o.textContent = 'D' + pair[0] + ' — ' + pair[1];
      districtGroup.appendChild(o);
    });
  }

  var townGroup = byId('szt-browse-towns');
  if (townGroup) {
    TOWNS.forEach(function (t) {
      var o = document.createElement('option');
      o.value = '/pages/schools-' + slugifyTown(t);
      o.textContent = t;
      townGroup.appendChild(o);
    });
  }

  var browseEl = byId('szt-browse');
  if (browseEl) {
    browseEl.addEventListener('change', function (e) {
      if (e.target.value) window.location.href = e.target.value;
    });
  }

  render();
})();
